/**
 * Client-side timeline 串播器 (§6.5.2) — WYSIWYG 预览。[REFACTOR-2026-06-08]
 *
 * 播放序列 = [前贴?] + 选片片段… + [后贴?]，尽量在预览里体现精修改动：
 * - 变速：video.playbackRate = clip.playback_speed（仅片段）
 * - 字幕：按 caption_style（颜色/字号/描边/位置）叠加 clip.caption（仅片段）
 * - 转场：进入片段时用 CSS 叠层近似（叠化/闪白/闪黑）；改转场即时闪一下预览
 * - BGM：所选曲目用 <audio> 跟随播放（音量；scope=clips 时只在选中段出声）
 * - 前/后贴：从素材库取 asset.url 直接播放（贴在序列首/尾）
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Play, Pause, SkipBack, SkipForward } from 'lucide-react'

import { dramaApi } from '@/lib/api/drama'
import { api } from '@/lib/api'
import type {
  BgmSpec,
  CaptionStyle,
  DramaSegment,
  IntroOutroClip,
  TimelineItem,
} from '@/types/drama'
import { Button } from '@/components/ui/button'

interface Props {
  projectId: string
  clips: TimelineItem[]
  segments: DramaSegment[]
  activeOrder?: number
  onActiveOrderChange?: (order: number) => void
  bgm?: BgmSpec
  captionStyle?: CaptionStyle
  intro?: IntroOutroClip | null
  outro?: IntroOutroClip | null
}

const PREVIEW_SCALE = 520 / 1280
const FX_LABEL: Record<string, string> = {
  dissolve: '转场：叠化',
  flash_white: '转场：闪白',
  flash_black: '转场：闪黑',
}

type PlayItem =
  | { kind: 'segment'; clip: TimelineItem; key: string }
  | { kind: 'asset'; slot: 'intro' | 'outro'; assetId: string; name?: string | null; key: string }

function segmentDuration(segment: DramaSegment | undefined): number {
  return segment ? segment.end_seconds - segment.start_seconds : 0
}
function effectiveEnd(item: TimelineItem, segment: DramaSegment | undefined): number {
  return item.source_end_offset ?? segmentDuration(segment)
}

export function TimelinePlayer({
  projectId,
  clips,
  segments,
  activeOrder,
  onActiveOrderChange,
  bgm,
  captionStyle,
  intro,
  outro,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const bgmRef = useRef<HTMLAudioElement | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const [overallProgress, setOverallProgress] = useState(0)
  const [fx, setFx] = useState<{ key: number; type: string }>({ key: 0, type: 'hard_cut' })
  const [assetMeta, setAssetMeta] = useState<Record<string, { url: string | null; dur: number }>>({})

  const segmentMap = useMemo(() => new Map(segments.map((s) => [s.segment_code, s])), [segments])
  const timeline = clips || []

  // Build the playback sequence: intro + segments + outro.
  const playlist: PlayItem[] = useMemo(() => {
    const items: PlayItem[] = []
    if (intro?.asset_id) items.push({ kind: 'asset', slot: 'intro', assetId: intro.asset_id, name: intro.name, key: 'intro' })
    for (const c of timeline) items.push({ kind: 'segment', clip: c, key: `seg-${c.order}-${c.segment_code}` })
    if (outro?.asset_id) items.push({ kind: 'asset', slot: 'outro', assetId: outro.asset_id, name: outro.name, key: 'outro' })
    return items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, intro?.asset_id, outro?.asset_id])

  const current = playlist[currentIndex]
  const currentSegment =
    current?.kind === 'segment' ? segmentMap.get(current.clip.segment_code) : undefined

  // Resolve intro/outro asset URLs + durations.
  useEffect(() => {
    const ids = [intro?.asset_id, outro?.asset_id].filter(Boolean) as string[]
    ids.forEach(async (id) => {
      if (assetMeta[id]) return
      try {
        const a = await api.getAsset(id)
        setAssetMeta((m) => ({ ...m, [id]: { url: a.url, dur: a.duration_seconds ?? 0 } }))
      } catch {
        /* ignore */
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intro?.asset_id, outro?.asset_id])

  const itemDuration = useCallback(
    (it: PlayItem): number => {
      if (it.kind === 'segment') {
        const seg = segmentMap.get(it.clip.segment_code)
        return Math.max(0, effectiveEnd(it.clip, seg) - (it.clip.source_start_offset || 0))
      }
      return assetMeta[it.assetId]?.dur ?? 0
    },
    [segmentMap, assetMeta],
  )

  const totalDuration = playlist.reduce((sum, it) => sum + itemDuration(it), 0)

  // External "select item" → jump to that segment in the playlist.
  useEffect(() => {
    if (activeOrder == null) return
    const idx = playlist.findIndex((it) => it.kind === 'segment' && it.clip.order === activeOrder)
    if (idx >= 0 && idx !== currentIndex) setCurrentIndex(idx)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrder])

  // Load the current item into <video>.
  useEffect(() => {
    const vid = videoRef.current
    if (!vid || !current) return
    setLoading(true)
    let active = true
    let objectUrl: string | null = null

    if (current.kind === 'segment') {
      const startOffset = current.clip.source_start_offset || 0
      const speed = Math.max(0.5, Math.min(1.5, current.clip.playback_speed ?? 1))
      const tr = (current.clip.transition as string) || 'hard_cut'
      const onLoaded = () => {
        vid.currentTime = startOffset
        vid.playbackRate = speed
        setLoading(false)
        // Fire the transition FX once the clip is actually visible (firing it on
        // index change would play it under the loading black screen → invisible).
        if (tr !== 'hard_cut') setFx((f) => ({ key: f.key + 1, type: tr }))
        if (playing) vid.play().catch(() => undefined)
      }
      vid.addEventListener('loadedmetadata', onLoaded, { once: true })
      // [FIX-2026-06-09] Stream via a presigned URL (Range-capable, instant start)
      // instead of downloading the whole mezzanine blob → much faster preview.
      const fileUrl = dramaApi.segmentFileUrl(projectId, current.clip.segment_code)
      const loadBlob = () => {
        api.client
          .get(fileUrl, { responseType: 'blob' })
          .then((res) => {
            if (!active) return
            objectUrl = URL.createObjectURL(res.data as Blob)
            vid.src = objectUrl
          })
          .catch(() => active && setLoading(false))
      }
      api.client
        .get(fileUrl, { params: { as_url: 1 } })
        .then((res) => {
          if (!active) return
          const u = (res.data as { url?: string } | null)?.url
          if (u) vid.src = u
          else loadBlob()
        })
        .catch(() => active && loadBlob())
      return () => {
        active = false
        vid.removeEventListener('loadedmetadata', onLoaded)
        if (objectUrl) URL.revokeObjectURL(objectUrl)
      }
    }

    // asset (intro/outro): play asset.url directly
    const meta = assetMeta[current.assetId]
    const onLoaded = () => {
      vid.playbackRate = 1
      setLoading(false)
      if (playing) vid.play().catch(() => undefined)
    }
    vid.addEventListener('loadedmetadata', onLoaded, { once: true })
    if (meta?.url) vid.src = meta.url
    else setLoading(false)
    return () => {
      active = false
      vid.removeEventListener('loadedmetadata', onLoaded)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key, currentIndex, assetMeta])

  // Transition FX — fires on clip entry AND when the current clip's transition
  // is edited, so changes preview instantly.
  useEffect(() => {
    const tr = current?.kind === 'segment' ? (current.clip.transition as string) : 'hard_cut'
    if (tr && tr !== 'hard_cut') setFx((f) => ({ key: f.key + 1, type: tr }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, current?.kind === 'segment' ? current.clip.transition : null])

  // ── BGM: load once, sync play/pause + volume ──
  useEffect(() => {
    const aud = bgmRef.current
    if (!aud) return
    if (!bgm?.enabled || !bgm.track_id) {
      aud.removeAttribute('src')
      return
    }
    let active = true
    let objectUrl: string | null = null
    api.client
      .get(dramaApi.bgmFileUrl(projectId, bgm.track_id), { responseType: 'blob' })
      .then((res) => {
        if (!active) return
        objectUrl = URL.createObjectURL(res.data as Blob)
        aud.src = objectUrl
        aud.loop = true
        if (playing) aud.play().catch(() => undefined)
      })
      .catch(() => undefined)
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgm?.enabled, bgm?.track_id, projectId])

  useEffect(() => {
    const aud = bgmRef.current
    if (!aud || !aud.src) return
    if (playing) aud.play().catch(() => undefined)
    else aud.pause()
  }, [playing])

  useEffect(() => {
    const aud = bgmRef.current
    if (!aud) return
    const vol = Math.max(0, Math.min(1, bgm?.volume ?? 0.15))
    const scoped = (bgm?.scope ?? 'whole') === 'clips'
    const inWindow =
      !scoped ||
      (current?.kind === 'segment' && (bgm?.clip_orders ?? []).includes(current.clip.order))
    aud.volume = inWindow ? vol : 0
  }, [bgm?.volume, bgm?.scope, bgm?.clip_orders, currentIndex, current])

  const advance = useCallback(() => {
    const vid = videoRef.current
    const nextIdx = currentIndex + 1
    if (nextIdx < playlist.length) {
      setCurrentIndex(nextIdx)
      const it = playlist[nextIdx]
      if (it.kind === 'segment') onActiveOrderChange?.(it.clip.order)
    } else {
      setPlaying(false)
      vid?.pause()
    }
  }, [currentIndex, playlist, onActiveOrderChange])

  const handleTimeUpdate = useCallback(() => {
    const vid = videoRef.current
    if (!vid || !current) return
    let endOff: number
    if (current.kind === 'segment') endOff = effectiveEnd(current.clip, currentSegment)
    else endOff = vid.duration || itemDuration(current)
    if (endOff && vid.currentTime >= endOff - 0.05) advance()

    let elapsed = 0
    for (let i = 0; i < currentIndex; i++) elapsed += itemDuration(playlist[i])
    const startOffset = current.kind === 'segment' ? current.clip.source_start_offset || 0 : 0
    elapsed += Math.max(0, vid.currentTime - startOffset)
    setOverallProgress(elapsed)
  }, [current, currentSegment, currentIndex, playlist, itemDuration, advance])

  const togglePlay = () => {
    const vid = videoRef.current
    if (!vid) return
    if (vid.paused) {
      vid.play()
      setPlaying(true)
    } else {
      vid.pause()
      setPlaying(false)
    }
  }

  const seekItem = (delta: number) => {
    const next = Math.min(playlist.length - 1, Math.max(0, currentIndex + delta))
    if (next === currentIndex) return
    setCurrentIndex(next)
    const it = playlist[next]
    if (it.kind === 'segment') onActiveOrderChange?.(it.clip.order)
    videoRef.current?.pause()
    setPlaying(false)
  }

  // Caption (segment items only)
  const cs = captionStyle
  const capPos = cs?.position ?? 'bottom'
  const captionWrapStyle: React.CSSProperties =
    capPos === 'top'
      ? { top: '8%', left: '50%', transform: 'translateX(-50%)' }
      : capPos === 'center'
      ? { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }
      : { bottom: '9%', left: '50%', transform: 'translateX(-50%)' }
  const captionTextStyle: React.CSSProperties = {
    color: cs?.font_color ?? '#FFFFFF',
    fontSize: Math.max(11, (cs?.font_size ?? 42) * PREVIEW_SCALE),
    fontWeight: 700,
    WebkitTextStroke:
      cs && cs.outline_width ? `${Math.max(0.5, cs.outline_width * PREVIEW_SCALE)}px ${cs.outline_color}` : undefined,
  }
  const activeCaption = current?.kind === 'segment' ? current.clip.caption || '' : ''
  const badge =
    current?.kind === 'asset' ? (current.slot === 'intro' ? '前贴' : '后贴') : null

  if (playlist.length === 0) {
    return (
      <div className="flex items-center justify-center bg-muted rounded-md h-64 text-muted-foreground text-sm">
        时间线为空 —— 拖入片段以开始。
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <style>{`
        @keyframes tlFlash { 0% { opacity: 1 } 100% { opacity: 0 } }
        @keyframes tlDissolve { 0% { opacity: .85 } 100% { opacity: 0 } }
        @keyframes tlLabel { 0% { opacity: 0 } 15% { opacity: 1 } 70% { opacity: 1 } 100% { opacity: 0 } }
        .tlfx-flash_white { background:#fff; animation: tlFlash .5s ease-out forwards; }
        .tlfx-flash_black { background:#000; animation: tlFlash .5s ease-out forwards; }
        .tlfx-dissolve    { background:#000; animation: tlDissolve .6s ease-out forwards; }
        .tlfx-label { animation: tlLabel 1.4s ease-out forwards; }
      `}</style>

      <div className="relative bg-black rounded-md overflow-hidden aspect-[9/16] max-h-[520px] mx-auto">
        <video ref={videoRef} onTimeUpdate={handleTimeUpdate} className="w-full h-full" playsInline />
        <audio ref={bgmRef} className="hidden" />

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
        )}
        {fx.type !== 'hard_cut' && (
          <>
            <div key={fx.key} className={`absolute inset-0 pointer-events-none tlfx-${fx.type}`} />
            <div
              key={`lbl-${fx.key}`}
              className="tlfx-label absolute top-10 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[11px] pointer-events-none"
            >
              {FX_LABEL[fx.type] || fx.type}
            </div>
          </>
        )}
        {activeCaption && (
          <div className="absolute max-w-[88%] text-center leading-tight" style={captionWrapStyle}>
            <span style={captionTextStyle}>{activeCaption}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 px-2 py-1 rounded bg-black/60 text-white text-xs">
          {currentIndex + 1} / {playlist.length}
          {badge && <span className="ml-1.5 text-spectrum-emerald">{badge}</span>}
        </div>
        {bgm?.enabled && bgm.track_id && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-[10px]">♪ BGM</div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" onClick={() => seekItem(-1)} disabled={currentIndex === 0}>
          <SkipBack className="w-4 h-4" />
        </Button>
        <Button onClick={togglePlay} size="sm">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={() => seekItem(1)} disabled={currentIndex === playlist.length - 1}>
          <SkipForward className="w-4 h-4" />
        </Button>
        <div className="text-xs text-muted-foreground ml-3 font-mono">
          {overallProgress.toFixed(1)}s / {totalDuration.toFixed(1)}s
        </div>
      </div>

      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-spectrum-magenta transition-all"
          style={{ width: totalDuration > 0 ? `${(overallProgress / totalDuration) * 100}%` : '0%' }}
        />
      </div>
    </div>
  )
}
